// Dashboard.jsx - COMPLETE VERSION WITH LEGEND POSITION FIX
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useNavigate } from 'react-router-dom';
import {
  Plus, LogOut, Database, Zap, Folder, Save, Download, Upload, Moon, Sun, Settings, X,
  Grid, BarChart3, Activity, TrendingUp, Layers, MoreHorizontal, FileText, Shield, Maximize2, Minimize2,
  ChevronDown, Bell, Filter, Menu
} from 'lucide-react';
import authService from '../../services/auth';
import { APP_VERSION } from '../../version';
import questdbService from '../../services/questdb';
import mysqlService from '../../services/mysql';
import dashboardService from '../../services/dashboardService';
import realtimeService from '../../services/Realtimeservice';
import DashboardModal from './DashboardModal';
import PanelConfigModal from './PanelConfigModal';
import QuestDBPanel from './QuestDBPanel';
import ScadaDesigner from './ScadaDesigner';
import ScadaPanel from './ScadaPanel';
import DashboardFiltersBar from './DashboardFiltersBar';
import VariableManager from './VariableManager';
import ShareDashboardModal from './ShareDashboardModal';
import LockedFeature from '../common/LockedFeature.jsx';
import RequestAccessModal from '../common/RequestAccessModal.jsx';
import { GRID_COLS, ROW_HEIGHT, DARK_COLORS } from './constants';
import { DashboardFilterProvider, useCrossFilters } from '../../services/dashboardFilterBus.jsx';
import SlicerPanel from './SlicerPanel';
import SlicerConfigModal from './SlicerConfigModal';
import BookmarksBar from './BookmarksBar';
import PagesTabBar, { normalizePages } from './PagesTabBar';
import ThemePicker from './ThemePicker';

// Wrapper that pulls cross-filter state from context and injects it into
// QuestDBPanel. Lives inside <DashboardFilterProvider>.
function ConnectedQuestDBPanel({ panelId, themePalette, onHidePanel, ...props }) {
  const { filters, filtersFor, emit, clearOne } = useCrossFilters();
  // Full active filter set — used ONLY for drill-through, where we want to
  // carry the entire dashboard's filter state to the target regardless of
  // which panel the drill button lives on. `crossFilters` (below) is still
  // scoped to "not self" so a panel doesn't filter itself into a one-row
  // island from its own bar-click emit.
  const allActiveFilters = React.useMemo(
    () => Object.values(filters || {}),
    [filters]
  );
  return (
    <QuestDBPanel
      {...props}
      themePalette={themePalette}
      onHidePanel={onHidePanel}
      crossFilters={filtersFor(panelId)}
      allActiveFilters={allActiveFilters}
      onEmitFilter={(column, value, operator) => emit(panelId, column, value, operator)}
      onClearCrossFilters={() => clearOne(panelId)}
    />
  );
}

// Seeds the filter bus from a drill-through payload when a dashboard loads.
// The QuestDBPanel drill-through button stashes the payload in sessionStorage
// under `miralys.drillFilter.<dashboardId>` and dispatches a custom event;
// this seeder reads (and clears) the payload on mount. Runs once per dashboard
// mount because the provider is keyed by dashboard id.
function DrillFilterSeeder({ dashboardId }) {
  const { restore } = useCrossFilters();
  React.useEffect(() => {
    if (typeof window === 'undefined' || !dashboardId) return;
    const key = `miralys.drillFilter.${dashboardId}`;
    let raw;
    try { raw = sessionStorage.getItem(key); } catch { raw = null; }
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) restore(parsed);
    } catch { /* malformed — ignore */ }
    try { sessionStorage.removeItem(key); } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId]);
  return null;
}

const getPlantId = (user) => {
  if (!user) {
    console.error('❌ No user object provided');
    return null;
  }
  
  if (user.plantId) {
    console.log('✅ Found plantId:', user.plantId);
    return user.plantId;
  }
  
  if (user.plantAccess && Array.isArray(user.plantAccess) && user.plantAccess.length > 0) {
    const plantId = user.plantAccess[0].plantId;
    console.log('✅ Found plantId from plantAccess:', plantId);
    return plantId;
  }
  
  console.error('❌ No plantId found in user object:', user);
  return null;
};

export default function Dashboard({ onLogout, onReporting, onAlarms, onAdmin }) {
  const navigate = useNavigate();
  const user = authService.getUser();

  // Role-based access: only plant-level 'admin' (or global admin) can create/edit/delete
  const canEdit = useMemo(() => {
    if (!user) return false;
    if (user.globalRole === 'admin' || user.globalRole === 'superadmin') return true;
    const plantId = getPlantId(user);
    const access = (user.plantAccess || []).find(pa => pa.plantId === plantId);
    if (access?.role === 'admin') return true;
    // Feature-based edit access: panel or scada feature unlocks dashboard editing
    const features = user.features || {};
    return !!(features.panel || features.scada);
  }, [user]);

  const [dashboards, setDashboards] = useState([]);
  const [currentDashboard, setCurrentDashboard] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [showScadaModal, setShowScadaModal] = useState(false);
  const [showSlicerModal, setShowSlicerModal] = useState(false);
  const [editingSlicer, setEditingSlicer] = useState(null);
  // Active page for multi-tab dashboards. Resets to 'default' whenever the
  // dashboard changes so a saved active page doesn't leak across switches.
  const [activePageId, setActivePageId] = useState('default');
  // Spotlight bookmarks: transient list of panel ids that are currently
  // hidden. Not persisted on the dashboard itself — captured into bookmarks.
  const [hiddenPanelIds, setHiddenPanelIds] = useState([]);
  // Drill-through history — stack of { id, name } for "← Back" chip.
  const [drillHistory, setDrillHistory] = useState([]);
  // Panels for the active page only. Legacy panels (no pageId) belong to
  // 'default'. CRUD handlers still operate on the *full* panels array — the
  // filter here is display-only.
  const visiblePanels = useMemo(() => {
    if (!currentDashboard) return [];
    const hidden = new Set(hiddenPanelIds || []);
    return (currentDashboard.panels || [])
      .filter(p => (p.pageId || 'default') === activePageId)
      .filter(p => !hidden.has(p.id));
  }, [currentDashboard, activePageId, hiddenPanelIds]);
  const [editingPanel, setEditingPanel] = useState(null);
  const [editingScadaDiagram, setEditingScadaDiagram] = useState(null);
  const [availableTables, setAvailableTables] = useState([]);
  const gridContainerRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [loading, setLoading] = useState(true);
  const [syncError, setSyncError] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('miralys_dark_mode');
    return saved ? JSON.parse(saved) : true;
  });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Dashboard-level template variables
  const [dashboardVariables, setDashboardVariables] = useState([]);
  const [variableValues, setVariableValues] = useState({});
  const [variableOptions, setVariableOptions] = useState({});
  const [variableOptionsLoading, setVariableOptionsLoading] = useState({});
  const [adhocFilters, setAdhocFilters] = useState([]);
  const [adhocColumnOptions, setAdhocColumnOptions] = useState([]);
  const [showVariableManager, setShowVariableManager] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareLockedModal, setShowShareLockedModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const [showDashListDropdown, setShowDashListDropdown] = useState(false);
  const dashListDropdownRef = useRef(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const mobileMenuRef = useRef(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenPanel, setFullscreenPanel] = useState(null); // which SCADA panel is fullscreened

  // Responsive grid columns — memoized, avoids recalculation on unrelated re-renders
  const responsiveGridCols = useMemo(() => {
    if (windowWidth < 640) return 1;
    if (windowWidth < 1024) return 2;
    if (windowWidth < 1440) return 4;
    return GRID_COLS;
  }, [windowWidth]);

  const isMobile = windowWidth < 768;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Close dash list dropdown on outside click
  useEffect(() => {
    if (!showDashListDropdown) return;
    const handleClickOutside = (e) => {
      if (dashListDropdownRef.current && !dashListDropdownRef.current.contains(e.target)) {
        setShowDashListDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDashListDropdown]);

  // Close mobile menu on outside click
  useEffect(() => {
    if (!showMobileMenu) return;
    const handleClickOutside = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMobileMenu]);

  // Close user menu on outside click
  useEffect(() => {
    if (!showUserMenu) return;
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Enter/Escape key exits fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter') exitFullscreen();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Sync state when native fullscreen is exited by browser (F11, ESC)
  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement) setIsFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const enterFullscreen = (panel) => {
    setIsFullscreen(true);
    if (panel) setFullscreenPanel(panel);
    document.documentElement.requestFullscreen?.().catch(() => {});
  };

  const exitFullscreen = () => {
    setIsFullscreen(false);
    setFullscreenPanel(null);
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  };

  // Navigation handler for SCADA nav buttons — stable reference prevents ScadaPanel re-renders
  const handleNavigate = useCallback((targetPanelId) => {
    let targetPanel = null;
    let targetDashboard = null;
    for (const d of dashboards) {
      const p = (d.panels || []).find(p => p.id === targetPanelId);
      if (p) { targetPanel = p; targetDashboard = d; break; }
    }
    if (!targetDashboard || !targetPanel) return;

    if (targetDashboard.id !== currentDashboard?.id) handleSelectDashboard(targetDashboard);

    // In fullscreen: update the displayed panel if target dashboard is SCADA-only
    if (isFullscreen) {
      const targetHasQuestDB = (targetDashboard.panels || []).some(p => p.type !== 'scada');
      if (!targetHasQuestDB) setFullscreenPanel(targetPanel);
      else setFullscreenPanel(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboards, currentDashboard?.id, isFullscreen]);

  // All SCADA panels across all dashboards (for nav button target dropdown) — memoized
  const allScadaPanels = useMemo(() => dashboards.flatMap(d =>
    (d.panels || []).filter(p => p.type === 'scada').map(p => ({
      id: p.id, title: p.title || 'Untitled', dashboardId: d.id, dashboardName: d.name || 'Dashboard'
    }))
  ), [dashboards]);

  useEffect(() => {
    console.log('👤 Current user object:', user);
    console.log('🏭 Extracted plantId:', getPlantId(user));
  }, []);

  // Initialize WebSocket pub/sub connection
  useEffect(() => {
    const token = authService.getToken();
    if (token) {
      realtimeService.connect(token).catch(err => {
        console.warn('[Dashboard] WS connection failed, using HTTP fallback:', err.message);
      });
    }
  }, []);

  useEffect(() => {
    const fetchTables = async () => {
      const dashPlantId = currentDashboard?.plantId || getPlantId(user);
      if (!dashPlantId) return;
      try {
        const result = await questdbService.getTables(dashPlantId);
        const tableNames = result.map(row => row.table_name);
        setAvailableTables(tableNames);
      } catch (error) {
        // Agent for this plant may be offline (503). Don't spam the console
        // on dashboard switch — surface once at warn level so it's still
        // discoverable.
        if (error?.response?.status === 503) {
          console.warn(`[dashboard] QuestDB agent for plant ${dashPlantId} is offline — table list unavailable.`);
        } else {
          console.error('Error fetching tables:', error?.message || error);
        }
        setAvailableTables([]);
      }
    };
    fetchTables();
  }, [currentDashboard?.plantId]);

  useEffect(() => {
    loadDashboards();
  }, []);

  // Sync dashboard variables and ad-hoc columns when dashboard changes
  useEffect(() => {
    if (!currentDashboard) return;
    const vars = currentDashboard.variables || [];
    setDashboardVariables(vars);
    const initialValues = {};
    vars.forEach(v => {
      // Seed sliders with their min value (or explicit defaultValue) so the
      // very first render doesn't send a literal `$name` to the DB — a
      // slider always has a value, even before the user drags it.
      if (v.type === 'slider') {
        const raw = v.defaultValue !== undefined && v.defaultValue !== '' ? v.defaultValue : (v.min ?? 0);
        initialValues[v.id] = String(raw);
      } else {
        initialValues[v.id] = v.defaultValue || '';
      }
    });
    setVariableValues(initialValues);
    setAdhocFilters([]);
    fetchAllVariableOptions(vars, currentDashboard);
    fetchAdhocColumnOptions(currentDashboard.panels || [], currentDashboard);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDashboard?.id]);

  const loadDashboards = async () => {
    setLoading(true);
    setSyncError(null);
    
    try {
      const remoteDashboards = await dashboardService.syncDashboards();
      
      if (remoteDashboards && remoteDashboards.length > 0) {
        setDashboards(remoteDashboards);
        
        const defaultDashboard = remoteDashboards.find(d => d.isDefault);
        const activeDashboardId = localStorage.getItem('miralys_active_dashboard');
        const activeDashboard = remoteDashboards.find(d => d.id === activeDashboardId);
        
        setCurrentDashboard(activeDashboard || defaultDashboard || remoteDashboards[0]);
      } else {
        await createDefaultDashboard();
      }
    } catch (error) {
      console.error('Error loading dashboards:', error);
      setSyncError('Failed to load dashboards. Using offline mode.');
      
      const saved = localStorage.getItem('miralys_dashboards');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed && parsed.length > 0) {
            setDashboards(parsed);
            setCurrentDashboard(parsed[0]);
          } else {
            createDefaultDashboardOffline();
          }
        } catch (e) {
          console.error('Error parsing localStorage dashboards:', e);
          createDefaultDashboardOffline();
        }
      } else {
        createDefaultDashboardOffline();
      }
    } finally {
      setLoading(false);
    }
  };

  const createDefaultDashboard = async () => {
    try {
      const plantId = getPlantId(user);
      
      if (!plantId) {
        console.error('❌ No plantId found for user - creating offline dashboard');
        createDefaultDashboardOffline();
        return;
      }

      console.log('📝 Creating default dashboard for plant:', plantId);
      const newDashboard = await dashboardService.createDashboard(
        'Default Dashboard',
        plantId,
        [],
        true
      );

      setDashboards([newDashboard]);
      setCurrentDashboard(newDashboard);
      console.log('✅ Default dashboard created successfully');
    } catch (error) {
      console.error('❌ Error creating default dashboard:', error);
      createDefaultDashboardOffline();
    }
  };

  const createDefaultDashboardOffline = () => {
    console.log('📝 Creating offline default dashboard');
    const defaultDashboard = {
      id: `dashboard_${Date.now()}`,
      name: 'Default Dashboard',
      panels: []
    };
    setDashboards([defaultDashboard]);
    setCurrentDashboard(defaultDashboard);
    try { localStorage.setItem('miralys_dashboards', JSON.stringify([defaultDashboard])); } catch (e) { console.warn('localStorage quota exceeded, skipping backup save'); }
  };

  // Debounced localStorage backup — avoids blocking JSON.stringify on every rapid save
  const lsTimerRef = useRef(null);
  useEffect(() => {
    if (lsTimerRef.current) clearTimeout(lsTimerRef.current);
    lsTimerRef.current = setTimeout(() => {
      try {
        if (dashboards.length > 0) {
          localStorage.setItem('miralys_dashboards', JSON.stringify(dashboards));
        }
        if (currentDashboard) {
          localStorage.setItem('miralys_active_dashboard', currentDashboard.id);
        }
      } catch (e) {
        console.warn('localStorage quota exceeded, skipping backup save');
      }
    }, 1000);
    return () => clearTimeout(lsTimerRef.current);
  }, [dashboards, currentDashboard]);

  useEffect(() => {
    localStorage.setItem('miralys_dark_mode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const theme = darkMode ? {
    bg: '#0a0e1a',
    bgSecondary: '#111827',
    card: '#1a1f35',
    cardHover: '#252b45',
    hover: '#1e2538',
    text: '#f8fafc',
    textSecondary: '#cbd5e1',
    textMuted: '#94a3b8',
    border: '#2d3548',
    borderLight: '#3f4a61',
    accent: '#6366f1',
    accentHover: '#7c3aed',
    success: '#10b981',
    danger: '#ef4444',
    warning: '#f59e0b'
  } : {
    bg: '#f9fafb',
    bgSecondary: '#ffffff',
    card: '#ffffff',
    cardHover: '#f3f4f6',
    hover: '#f3f4f6',
    text: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    border: '#e2e8f0',
    borderLight: '#cbd5e1',
    accent: '#6366f1',
    accentHover: '#7c3aed',
    success: '#10b981',
    danger: '#ef4444',
    warning: '#f59e0b'
  };

  const handleLogout = async () => {
    await authService.logout();
    onLogout();
  };

  const handleAddPanel = () => {
    setEditingPanel(null);
    setShowConfigModal(true);
  };

  const handleAddScada = () => {
    setEditingScadaDiagram(null);
    setShowScadaModal(true);
  };

  const handleAddSlicer = () => {
    setEditingSlicer(null);
    setShowSlicerModal(true);
  };

  // Drill-through — QuestDBPanel dispatches a custom event when a user
  // clicks the drill button. Switch to the target dashboard by id; the
  // provider's key={dashboardId} triggers DrillFilterSeeder to apply the
  // payload that the panel has already written to sessionStorage.
  useEffect(() => {
    const onDrill = (e) => {
      const targetId = e?.detail?.targetId;
      if (!targetId) return;
      const target = dashboards.find(d => (d._id === targetId) || (d.id === targetId));
      if (!target) {
        console.warn('[drill-through] target dashboard not found:', targetId, 'known ids:', dashboards.map(d => d.id));
        return;
      }
      console.log('[drill-through] switching to', target.id, target.name);
      // Push origin onto the back-stack for the "← Back" chip on the target.
      if (currentDashboard && currentDashboard.id !== target.id) {
        setDrillHistory(prev => [...prev, {
          id: currentDashboard.id,
          name: currentDashboard.name,
          fromPanelTitle: e?.detail?.fromPanelTitle
        }]);
      }
      try { localStorage.setItem('miralys_active_dashboard', target.id); } catch { /* noop */ }
      handleSelectDashboard(target);
    };
    window.addEventListener('miralys:drillThrough', onDrill);
    return () => window.removeEventListener('miralys:drillThrough', onDrill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboards, currentDashboard]);

  // Handle the "← Back" chip click on the target dashboard.
  const drillBack = () => {
    setDrillHistory(prev => {
      const stack = [...prev];
      const last = stack.pop();
      if (last) {
        const target = dashboards.find(d => d.id === last.id);
        if (target) handleSelectDashboard(target);
      }
      return stack;
    });
  };

  // Bookmarks — persisted at the dashboard level. The bar handles all UI;
  // we just plumb save (whole array replace) and variable restore.
  const handleUpdateBookmarks = (nextBookmarks) => {
    if (!currentDashboard) return;
    updateDashboard({ ...currentDashboard, bookmarks: nextBookmarks });
  };
  const handleApplyBookmarkVariables = (vars) => {
    // Merge — bookmark's variables win, but keep any variables not in the
    // snapshot so we don't null-out something the dashboard just declared.
    setVariableValues(prev => ({ ...prev, ...vars }));
  };

  // Dashboard theme — palette override that all panels pick up via prop.
  const handleUpdateTheme = ({ palette, name }) => {
    if (!currentDashboard) return;
    updateDashboard({
      ...currentDashboard,
      themePalette: palette || [],
      themeName: name || ''
    });
  };

  // Pages / tabs — the tab bar handles UI, we mutate dashboard.pages.
  const handleAddPage = (name) => {
    if (!currentDashboard) return;
    const current = normalizePages(currentDashboard.pages || []);
    const nextOrder = current.length ? Math.max(...current.map(p => p.order ?? 0)) + 1 : 0;
    const newPage = { id: `pg_${Date.now()}`, name, order: nextOrder };
    // If the dashboard was implicit-single-page, materialize 'default' first
    // so the existing panels stay attached to it.
    const materialized = current.length ? current : [{ id: 'default', name: 'Page 1', order: 0 }];
    updateDashboard({ ...currentDashboard, pages: [...materialized, newPage] });
    setActivePageId(newPage.id);
  };
  const handleRenamePage = (pageId, name) => {
    if (!currentDashboard) return;
    const current = normalizePages(currentDashboard.pages || []);
    const nextPages = current.map(p => p.id === pageId ? { ...p, name } : p);
    updateDashboard({ ...currentDashboard, pages: nextPages });
  };
  const handleDeletePage = (pageId) => {
    if (!currentDashboard) return;
    const current = normalizePages(currentDashboard.pages || []);
    if (current.length <= 1) return;
    const nextPages = current.filter(p => p.id !== pageId);
    // Also drop all panels that lived on this page.
    const nextPanels = (currentDashboard.panels || []).filter(p => (p.pageId || 'default') !== pageId);
    updateDashboard({ ...currentDashboard, pages: nextPages, panels: nextPanels });
    if (activePageId === pageId) setActivePageId(nextPages[0].id);
  };

  const handleSaveSlicer = (slicerData) => {
    if (!currentDashboard) return;
    let updatedPanels;
    if (editingSlicer) {
      updatedPanels = currentDashboard.panels.map(p =>
        p.id === editingSlicer.id ? { ...p, ...slicerData, id: p.id } : p
      );
    } else {
      const newSlicer = {
        ...slicerData,
        id: `slicer_${Date.now()}`,
        x: 0, y: 0,
        width:  slicerData.width  ?? 3,
        height: slicerData.height ?? 3,
        pageId: activePageId
      };
      updatedPanels = [...currentDashboard.panels, newSlicer];
    }
    updateDashboard({ ...currentDashboard, panels: updatedPanels });
    setEditingSlicer(null);
  };

  const handleEdit = (panel) => {
    if (panel.type === 'scada') {
      setEditingScadaDiagram(panel);
      setShowScadaModal(true);
    } else if (panel.type === 'slicer') {
      setEditingSlicer(panel);
      setShowSlicerModal(true);
    } else {
      setEditingPanel(panel);
      setShowConfigModal(true);
    }
  };

  const handleDelete = (panelId) => {
    if (!currentDashboard) return;
    const updated = {
      ...currentDashboard,
      panels: currentDashboard.panels.filter(p => p.id !== panelId)
    };
    updateDashboard(updated);
  };

  const handleDuplicate = (panel) => {
    if (!currentDashboard) return;
    const newPanel = {
      ...panel,
      id: `panel_${Date.now()}`,
      title: `${panel.title} (Copy)`,
      x: (panel.x + 2) % GRID_COLS,
      y: panel.y + 1,
      // Duplicate onto the *currently visible* page rather than the source
      // panel's original page, so the copy shows up right away.
      pageId: activePageId
    };
    const updated = {
      ...currentDashboard,
      panels: [...currentDashboard.panels, newPanel]
    };
    updateDashboard(updated);
  };

  // Silent auto-save — updates data only, never closes the designer modal
  const handleAutoSavePanel = (panelData) => {
    if (!currentDashboard || !editingScadaDiagram) return;
    const updatedPanels = currentDashboard.panels.map(p =>
      p.id === editingScadaDiagram.id ? { ...p, ...panelData, id: p.id } : p
    );
    updateDashboard({ ...currentDashboard, panels: updatedPanels });
  };

  // ✅ FIXED: handleSavePanel with explicit legendPosition preservation
  const handleSavePanel = (panelData) => {
    if (!currentDashboard) return;
    
    console.log('💾 SAVE PANEL - Received panelData:', {
      id: panelData.id,
      title: panelData.title,
      legendPosition: panelData.legendPosition,
      showLegend: panelData.showLegend,
      vizType: panelData.vizType
    });
    
    let updatedPanels;
    if (editingPanel || editingScadaDiagram) {
      const editingId = editingPanel?.id || editingScadaDiagram?.id;
      updatedPanels = currentDashboard.panels.map(p => {
        if (p.id === editingId) {
          // ✅ CRITICAL FIX: Explicitly preserve all properties including legendPosition
          const mergedPanel = {
            ...p,              // Start with existing panel
            ...panelData,      // Override with new data
            id: p.id,          // Always preserve ID
            // ✅ Explicitly ensure critical properties are not lost
           // legendPosition: panelData.legendPosition || p.legendPosition || 'top',
           // showLegend: panelData.showLegend !== undefined ? panelData.showLegend : (p.showLegend !== undefined ? p.showLegend : true)
          };
          
          console.log('✅ MERGED PANEL:', {
            id: mergedPanel.id,
            title: mergedPanel.title,
            legendPosition: mergedPanel.legendPosition,
            showLegend: mergedPanel.showLegend
          });
          
          return mergedPanel;
        }
        return p;
      });
    } else {
      const newPanel = {
        ...panelData,
        id: `panel_${Date.now()}`,
        x: 0,
        y: Infinity, // RGL places it at the bottom automatically
        width: panelData.width || 4,
        height: panelData.height || 2,
        legendPosition: panelData.legendPosition,
        // Attach to currently active page so the new panel appears on the tab
        // the user was on when they clicked Add.
        pageId: activePageId
      };
      
      console.log('✅ NEW PANEL:', {
        id: newPanel.id,
        title: newPanel.title,
        legendPosition: newPanel.legendPosition
      });
      
      updatedPanels = [...currentDashboard.panels, newPanel];
    }

    const updated = {
      ...currentDashboard,
      panels: updatedPanels
    };
    
    console.log('📦 DASHBOARD UPDATE - Panels:', updated.panels.map(p => ({
      id: p.id,
      title: p.title,
      legendPosition: p.legendPosition
    })));
    
    updateDashboard(updated);
    
    setShowConfigModal(false);
    setShowScadaModal(false);
    setEditingPanel(null);
    setEditingScadaDiagram(null);
  };

  const handleResize = (panelId, newWidth, newHeight) => {
    if (!currentDashboard) return;
    const updated = {
      ...currentDashboard,
      panels: currentDashboard.panels.map(p =>
        p.id === panelId ? { ...p, width: newWidth, height: newHeight } : p
      )
    };
    updateDashboard(updated);
  };

  // Grid width tracking for react-grid-layout
  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setGridWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Save layout after drag or resize ends (not on every pixel move).
  //
  // react-grid-layout fires onDragStop / onResizeStop even for spurious
  // events — for example, when a click on a nested button (drill-through)
  // completes its mousedown→mouseup cycle without actually moving anything.
  // If we forwarded those to updateDashboard, the async save would
  // eventually call setCurrentDashboard(saved) and clobber any other state
  // change (like a drill-through) that happened in the same tick.
  // So: only save when the layout actually changed vs. the current panel
  // positions.
  const handleLayoutChange = (newLayout) => {
    if (!currentDashboard) return;
    const changed = newLayout.some(item => {
      const p = currentDashboard.panels.find(pp => pp.id === item.i);
      if (!p) return false;
      return (p.x ?? 0) !== item.x
          || (p.y ?? 0) !== item.y
          || (p.width  ?? 4) !== item.w
          || (p.height ?? 2) !== item.h;
    });
    if (!changed) return;
    const updatedPanels = currentDashboard.panels.map(p => {
      const item = newLayout.find(l => l.i === p.id);
      if (!item) return p;
      return { ...p, x: item.x, y: item.y, width: item.w, height: item.h };
    });
    const updated = { ...currentDashboard, panels: updatedPanels };
    updateDashboard(updated);
  };

  // Fetch distinct values for each query-type variable
  const fetchAllVariableOptions = async (variables, dashboard) => {
    if (!variables || variables.length === 0) return;
    const plantId = dashboard?.plantId || getPlantId(user);
    if (!plantId) return;

    for (const variable of variables) {
      if (variable.type === 'query' && variable.table && variable.column) {
        const isMySQL = variable.dbEngine === 'mysql';
        if (isMySQL && !variable.mysqlDatabase) {
          setVariableOptions(prev => ({ ...prev, [variable.id]: [] }));
          continue;
        }
        setVariableOptionsLoading(prev => ({ ...prev, [variable.id]: true }));
        try {
          const sql = isMySQL
            ? `SELECT DISTINCT \`${variable.column}\` FROM \`${variable.table}\` ORDER BY \`${variable.column}\` LIMIT 500`
            : `SELECT DISTINCT ${variable.column} FROM ${variable.table} ORDER BY ${variable.column} LIMIT 500`;
          const result = isMySQL
            ? await mysqlService.query(sql, variable.mysqlDatabase)
            : await questdbService.query(sql, plantId);
          const opts = result.map(row => String(row[variable.column])).filter(Boolean);
          setVariableOptions(prev => ({ ...prev, [variable.id]: opts }));
        } catch (err) {
          console.error(`Failed to fetch options for variable ${variable.name}:`, err);
          setVariableOptions(prev => ({ ...prev, [variable.id]: [] }));
        } finally {
          setVariableOptionsLoading(prev => ({ ...prev, [variable.id]: false }));
        }
      } else if (variable.type === 'custom') {
        setVariableOptions(prev => ({ ...prev, [variable.id]: (variable.values || []).filter(Boolean) }));
      }
    }
  };

  // Collect column names from all tables used by panels, for ad-hoc filter dropdown.
  // Each panel knows its own engine + (for MySQL) database, so we branch per panel.
  const fetchAdhocColumnOptions = async (panels, dashboard) => {
    if (!panels || panels.length === 0) return;
    const plantId = dashboard?.plantId || getPlantId(user);

    // Unique (engine, database, table) keys so we don't fetch the same shape twice
    const seen = new Set();
    const targets = [];
    for (const p of panels) {
      if (p.dataSource !== 'table' || !p.table) continue;
      const isMySQL = p.dbEngine === 'mysql';
      const key = `${isMySQL ? 'mysql' : 'questdb'}::${p.mysqlDatabase || ''}::${p.table}`;
      if (seen.has(key)) continue;
      seen.add(key);
      targets.push({ isMySQL, table: p.table, mysqlDatabase: p.mysqlDatabase });
    }

    const allColumns = new Set();
    for (const t of targets) {
      try {
        if (t.isMySQL) {
          if (!t.mysqlDatabase) continue;
          const result = await mysqlService.query(
            `SELECT * FROM \`${t.table}\` LIMIT 1`,
            t.mysqlDatabase
          );
          if (Array.isArray(result) && result.length > 0 && result[0] && typeof result[0] === 'object') {
            Object.keys(result[0]).forEach(col => allColumns.add(col));
          }
        } else {
          if (!plantId) continue;
          const result = await questdbService.query(`SELECT * FROM ${t.table} LIMIT 1`, plantId);
          if (Array.isArray(result) && result.length > 0 && result[0] && typeof result[0] === 'object') {
            Object.keys(result[0]).forEach(col => allColumns.add(col));
          }
        }
      } catch {
        // ignore per-table errors
      }
    }
    setAdhocColumnOptions([...allColumns].sort());
  };

  const handleVariableChange = (variableId, newValue) => {
    setVariableValues(prev => ({ ...prev, [variableId]: newValue }));
  };

  const handleSaveVariables = async (newVariables) => {
    if (!currentDashboard) return;

    // Optimistically update local state first
    setDashboardVariables(newVariables);
    const initialValues = {};
    newVariables.forEach(v => {
      initialValues[v.id] = variableValues[v.id] ?? v.defaultValue ?? '';
    });
    setVariableValues(initialValues);
    fetchAllVariableOptions(newVariables, currentDashboard);

    try {
      // Use dedicated PATCH /variables endpoint — bypasses Mongoose Mixed type issues
      const saved = await dashboardService.updateVariables(currentDashboard.id, newVariables);
      const updatedDashboard = { ...saved, variables: newVariables };
      setDashboards(prev => prev.map(d => d.id === updatedDashboard.id ? updatedDashboard : d));
      setCurrentDashboard(updatedDashboard);
    } catch (err) {
      console.error('Error saving variables:', err);
    }
    setShowVariableManager(false);
  };

  const handleAddAdhocFilter = () => {
    setAdhocFilters(prev => [
      ...prev,
      { id: `adhoc_${Date.now()}`, column: '', operator: '=', value: '' }
    ]);
  };

  const handleUpdateAdhocFilter = (id, key, value) => {
    setAdhocFilters(prev => prev.map(f => f.id === id ? { ...f, [key]: value } : f));
  };

  const handleRemoveAdhocFilter = (id) => {
    setAdhocFilters(prev => prev.filter(f => f.id !== id));
  };

  const updateDashboard = async (updatedDashboard) => {
    try {
      const saved = await dashboardService.updateDashboard(updatedDashboard.id, {
        name: updatedDashboard.name,
        panels: updatedDashboard.panels,
        variables: updatedDashboard.variables || [],
        bookmarks: updatedDashboard.bookmarks || [],
        pages: updatedDashboard.pages || [],
        themePalette: updatedDashboard.themePalette || [],
        themeName: updatedDashboard.themeName || ''
      });

      setDashboards(prev => prev.map(d => d.id === saved.id ? saved : d));
      setCurrentDashboard(saved);
    } catch (error) {
      console.error('Error updating dashboard:', error);

      // Dashboard not found on server (e.g. was created offline) — create it now
      if (error.message?.includes('not found') || error.message?.includes('Not Found')) {
        try {
          const plantId = updatedDashboard.plantId || getPlantId(user);
          const created = await dashboardService.createDashboard(
            updatedDashboard.name,
            plantId,
            updatedDashboard.panels || [],
            updatedDashboard.isDefault || false,
            updatedDashboard.variables || []
          );
          console.log('✅ Offline dashboard migrated to server:', created.id);
          setDashboards(prev => [...prev.filter(d => d.id !== updatedDashboard.id), created]);
          setCurrentDashboard(created);
          return;
        } catch (createError) {
          console.error('Error migrating offline dashboard:', createError);
        }
      }

      // Show user-friendly message for size limit errors
      if (error.message?.includes('too large') || error.message?.includes('size limit')) {
        alert('⚠️ Dashboard is too large to save!\n\nYour dashboard exceeds MongoDB\'s 16MB limit. To fix this:\n• Remove or compress large background images in SCADA panels\n• Reduce the number of custom image components\n• Split into multiple dashboards');
      }
      // Keep optimistic local update so user doesn't lose work in the UI
      setDashboards(prev => prev.map(d => d.id === updatedDashboard.id ? updatedDashboard : d));
      setCurrentDashboard(updatedDashboard);
    }
  };

  const handleSelectDashboard = (dashboard) => {
    setCurrentDashboard(dashboard);
    setShowDashboardModal(false);
    // Reset to first page — either the explicit first page or the implicit 'default'.
    const firstPage = normalizePages(dashboard?.pages || [])[0];
    setActivePageId(firstPage?.id || 'default');
  };

  const handleCreateDashboard = async (name, plantId) => {
    try {
      const effectivePlantId = plantId || getPlantId(user);
      if (!effectivePlantId) {
        throw new Error('No plantId found');
      }

      const newDashboard = await dashboardService.createDashboard(name, effectivePlantId, [], false);
      setDashboards([...dashboards, newDashboard]);
      setCurrentDashboard(newDashboard);
      setShowDashboardModal(false);
    } catch (error) {
      console.error('Error creating dashboard:', error);
      alert('⚠️ Failed to create dashboard. Please check your connection and try again.');
    }
  };

  const handleRenameDashboard = async (dashboardId, newName) => {
    try {
      const dashboard = dashboards.find(d => d.id === dashboardId);
      if (!dashboard) return;

      const updated = await dashboardService.updateDashboard(dashboardId, {
        name: newName,
        panels: dashboard.panels
      });

      setDashboards(dashboards.map(d => d.id === dashboardId ? updated : d));
      if (currentDashboard?.id === dashboardId) {
        setCurrentDashboard(updated);
      }
    } catch (error) {
      console.error('Error renaming dashboard:', error);
      const updatedDashboards = dashboards.map(d =>
        d.id === dashboardId ? { ...d, name: newName } : d
      );
      setDashboards(updatedDashboards);
      if (currentDashboard?.id === dashboardId) {
        setCurrentDashboard(updatedDashboards.find(d => d.id === dashboardId));
      }
    }
  };

  const handleDeleteDashboard = async (dashboardId) => {
    try {
      await dashboardService.deleteDashboard(dashboardId);
      const remaining = dashboards.filter(d => d.id !== dashboardId);
      setDashboards(remaining);

      if (currentDashboard?.id === dashboardId) {
        setCurrentDashboard(remaining[0] || null);
      }
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      alert('Failed to delete dashboard. Please try again.');
    }
  };

  const calculatePanelStyle = (panel) => {
    // Adjust panel width to fit responsive grid
    const adjustedWidth = Math.min(panel.width || 4, responsiveGridCols);
    const adjustedHeight = windowWidth < 640 ? 1 : (panel.height || 2);
    
    return {
      gridColumn: `span ${adjustedWidth}`,
      gridRow: `span ${adjustedHeight}`
    };
  };

  if (loading) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.bg,
        color: theme.text
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: `3px solid ${theme.border}`,
            borderTopColor: theme.accent,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ fontSize: '14px', color: theme.textMuted }}>Loading dashboards...</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardFilterProvider key={currentDashboard?._id || currentDashboard?.id || 'none'}>
    <DrillFilterSeeder dashboardId={currentDashboard?._id || currentDashboard?.id} />
    <div style={{
      width: '100vw',
      height: '100vh',
      background: theme.bg,
      color: theme.text,
      fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
     
      {/* Header — two rows */}
      <div style={{
        background: theme.card,
        flexShrink: 0
      }}>
        {/* Row 1: Logo + action buttons */}
        <div className="hdr-row1" style={{
          display: 'flex', alignItems: 'center',
          minHeight: 40, padding: '4px 12px', gap: 8,
          flexWrap: 'wrap', rowGap: 6
        }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
            <div style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src="/vite.svg" alt="Miralys" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: theme.text, letterSpacing: '-0.01em' }}>
              Miralys
              <span style={{ fontSize: 10, fontWeight: 500, color: theme.textMuted, marginLeft: 4 }}>v{APP_VERSION}</span>
            </span>
          </div>

          <div style={{ flex: 1 }} />

          {!isMobile && <>
          {/* Panel */}
          {(
            <LockedFeature feature="panel">
              <button
                onClick={handleAddPanel}
                disabled={!currentDashboard}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '5px 11px', borderRadius: 6, border: 'none',
                  background: currentDashboard ? theme.accent : theme.border,
                  color: '#fff', fontSize: 12, fontWeight: 600,
                  opacity: currentDashboard ? 1 : 0.5,
                  cursor: currentDashboard ? 'pointer' : 'not-allowed',
                  outline: 'none', flexShrink: 0,
                }}
              >
                <BarChart3 size={13} /> Panel
              </button>
            </LockedFeature>
          )}

          {/* Scada */}
          {(
            <LockedFeature feature="scada">
              <button
                onClick={handleAddScada}
                disabled={!currentDashboard}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '5px 11px', borderRadius: 6, border: 'none',
                  background: currentDashboard ? theme.success : theme.border,
                  color: '#fff', fontSize: 12, fontWeight: 600,
                  opacity: currentDashboard ? 1 : 0.5,
                  cursor: currentDashboard ? 'pointer' : 'not-allowed',
                  outline: 'none', flexShrink: 0,
                }}
              >
                <Layers size={13} /> Scada
              </button>
            </LockedFeature>
          )}

          {/* Slicer — dashboard-scoped filter widget that feeds the cross-filter bus */}
          {(
            <LockedFeature feature="panel">
              <button
                onClick={handleAddSlicer}
                disabled={!currentDashboard}
                title="Add a filter widget (dropdown, multi-select, date range, or search)"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '5px 11px', borderRadius: 6, border: 'none',
                  background: currentDashboard ? '#8b5cf6' : theme.border,
                  color: '#fff', fontSize: 12, fontWeight: 600,
                  opacity: currentDashboard ? 1 : 0.5,
                  cursor: currentDashboard ? 'pointer' : 'not-allowed',
                  outline: 'none', flexShrink: 0,
                }}
              >
                <Filter size={13} /> Slicer
              </button>
            </LockedFeature>
          )}

          {/* Reports */}
          {onReporting && (
            <LockedFeature feature="reports">
              <button className="hdr-btn hdr-btn-ghost" onClick={onReporting}>
                <FileText size={13} />
                <span className="btn-label">Reports</span>
              </button>
            </LockedFeature>
          )}

          {/* Alarms */}
          {onAlarms && (
            <LockedFeature feature="alarms">
              <button className="hdr-btn hdr-btn-ghost" onClick={onAlarms}>
                <Bell size={13} />
                <span className="btn-label">Alarms</span>
              </button>
            </LockedFeature>
          )}

          {/* Admin */}
          {onAdmin && (
            <button className="hdr-btn hdr-btn-ghost" onClick={onAdmin}>
              <Shield size={13} />
              <span className="btn-label">Admin</span>
            </button>
          )}

          {/* Dark mode toggle */}
          <button
            className="hdr-btn hdr-btn-ghost"
            onClick={toggleDarkMode}
          >
            {darkMode ? <Sun size={13} /> : <Moon size={13} />}
            <span className="btn-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
          </>}

          {/* Mobile hamburger menu */}
          {isMobile && (
            <div ref={mobileMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
              <button
                onClick={() => setShowMobileMenu(p => !p)}
                title="Menu"
                style={{
                  width: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: showMobileMenu ? theme.hover : 'transparent',
                  border: `1px solid ${theme.border}`, borderRadius: 8,
                  color: theme.text, cursor: 'pointer', outline: 'none'
                }}
              >
                <Menu size={17} />
              </button>
              {showMobileMenu && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                  background: theme.card, border: `1px solid ${theme.border}`,
                  borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                  zIndex: 9999, minWidth: 200, overflow: 'hidden', padding: 6
                }}>
                  {[
                    { show: true, feature: 'panel', icon: BarChart3, label: 'Add Panel', color: theme.accent, onClick: handleAddPanel, disabled: !currentDashboard },
                    { show: true, feature: 'scada', icon: Layers, label: 'Add Scada', color: theme.success, onClick: handleAddScada, disabled: !currentDashboard },
                    { show: true, feature: 'panel', icon: Filter, label: 'Add Slicer', color: '#8b5cf6', onClick: handleAddSlicer, disabled: !currentDashboard },
                    { show: !!onReporting, feature: 'reports', icon: FileText, label: 'Reports', onClick: onReporting },
                    { show: !!onAlarms, feature: 'alarms', icon: Bell, label: 'Alarms', onClick: onAlarms },
                    { show: !!onAdmin, feature: null, icon: Shield, label: 'Admin', onClick: onAdmin },
                    { show: true, feature: null, icon: darkMode ? Sun : Moon, label: darkMode ? 'Light Mode' : 'Dark Mode', onClick: toggleDarkMode },
                  ].filter(a => a.show).map((a, i) => {
                    const Icon = a.icon;
                    const row = (
                      <button
                        key={a.label}
                        onClick={() => { if (!a.disabled) { a.onClick?.(); setShowMobileMenu(false); } }}
                        disabled={a.disabled}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                          padding: '9px 10px', background: 'transparent', border: 'none',
                          borderRadius: 6, color: theme.text, fontSize: 13, fontWeight: 500,
                          cursor: a.disabled ? 'not-allowed' : 'pointer', opacity: a.disabled ? 0.4 : 1,
                          textAlign: 'left', outline: 'none'
                        }}
                        onMouseEnter={(e) => !a.disabled && (e.currentTarget.style.background = theme.hover)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <span style={{
                          width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: (a.color || theme.border) + '22', color: a.color || theme.textSecondary
                        }}>
                          <Icon size={14} />
                        </span>
                        {a.label}
                      </button>
                    );
                    return a.feature
                      ? <LockedFeature key={a.label} feature={a.feature}>{row}</LockedFeature>
                      : row;
                  })}
                </div>
              )}
            </div>
          )}

          {/* User avatar with dropdown (logout inside) */}
          <div ref={userMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setShowUserMenu(prev => !prev)}
              title="Account"
              style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: '#fff',
                border: '2px solid rgba(99,102,241,0.4)',
                cursor: 'pointer', outline: 'none', padding: 0
              }}
            >
              {(user?.name || user?.username || user?.email || 'U').charAt(0).toUpperCase()}
            </button>
            {showUserMenu && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                background: theme.card, border: `1px solid ${theme.border}`,
                borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                zIndex: 9999, minWidth: 180, overflow: 'hidden'
              }}>
                {/* User info */}
                <div style={{ padding: '12px 14px', borderBottom: `1px solid ${theme.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0
                    }}>
                      {(user?.name || user?.username || user?.email || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.text, lineHeight: 1.3 }}>
                        {user?.name || user?.username || 'User'}
                      </div>
                      {user?.email && (
                        <div style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.3 }}>{user.email}</div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Logout */}
                <button
                  onClick={() => { setShowUserMenu(false); handleLogout(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 9, width: '100%',
                    padding: '10px 14px',
                    background: 'transparent', border: 'none',
                    color: theme.danger, fontSize: 13, fontWeight: 500,
                    cursor: 'pointer', outline: 'none', textAlign: 'left'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Dashboard tabs */}
        <div style={{
          display: 'flex', alignItems: 'center',
          height: 36, padding: '0 12px', gap: 3,
          width: '100%', boxSizing: 'border-box'
        }}>
          {/* Scrollable tabs area */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 3,
            flex: 1, minWidth: 0, overflowX: 'auto',
            scrollbarWidth: 'none'
          }}>
            {dashboards.map((dashboard, tabIdx) => {
              const isActive = currentDashboard?.id === dashboard.id;
              return (
                <button
                  key={dashboard.id}
                  onClick={() => handleSelectDashboard(dashboard)}
                  title={dashboard.name}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '3px 12px',
                    background: isActive ? 'rgba(99,102,241,0.10)' : 'transparent',
                    border: 'none',
                    borderLeft: tabIdx === 0 ? `1px solid ${theme.border}` : 'none',
                    borderRight: `1px solid ${theme.border}`,
                    borderRadius: 0,
                    color: isActive ? '#a78bfa' : theme.textMuted,
                    cursor: 'pointer', fontSize: 12, fontWeight: isActive ? 600 : 500,
                    whiteSpace: 'nowrap', flexShrink: 0, outline: 'none',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => !isActive && (e.currentTarget.style.background = theme.hover)}
                  onMouseLeave={(e) => !isActive && (e.currentTarget.style.background = 'transparent')}
                >
                  <Grid size={11} style={{ flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>{dashboard.name}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    background: isActive ? 'rgba(99,102,241,0.25)' : theme.border,
                    color: isActive ? '#a78bfa' : theme.textMuted,
                    padding: '0px 5px', borderRadius: 8, flexShrink: 0
                  }}>
                    {dashboard.panels?.length || 0}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Dashboard list dropdown — pinned right, left of + */}
          <div ref={dashListDropdownRef} style={{ position: 'relative', flexShrink: 0, marginLeft: 4 }}>
            <button
              onClick={() => setShowDashListDropdown(prev => !prev)}
              title="All dashboards"
              style={{
                padding: '3px 7px', background: showDashListDropdown ? theme.hover : 'transparent',
                border: `1px solid ${theme.border}`, borderRadius: 6,
                color: theme.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', outline: 'none'
              }}
            >
              <ChevronDown size={13} />
            </button>
            {showDashListDropdown && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                background: theme.card, border: `1px solid ${theme.border}`,
                borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                zIndex: 9999, minWidth: 200, maxHeight: 320, overflowY: 'auto', padding: 4
              }}>
                <div style={{ padding: '5px 10px 4px', fontSize: 10, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  All Dashboards ({dashboards.length})
                </div>
                {dashboards.map((dashboard) => {
                  const isActive = currentDashboard?.id === dashboard.id;
                  return (
                    <button
                      key={dashboard.id}
                      onClick={() => { handleSelectDashboard(dashboard); setShowDashListDropdown(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                        padding: '7px 10px',
                        background: isActive ? 'rgba(99,102,241,0.12)' : 'transparent',
                        color: isActive ? '#a78bfa' : theme.text,
                        border: 'none', borderRadius: 6, fontSize: 12,
                        fontWeight: isActive ? 600 : 400,
                        cursor: 'pointer', textAlign: 'left', outline: 'none'
                      }}
                      onMouseEnter={(e) => !isActive && (e.currentTarget.style.background = theme.hover)}
                      onMouseLeave={(e) => !isActive && (e.currentTarget.style.background = 'transparent')}
                    >
                      <Grid size={12} color={isActive ? '#a78bfa' : theme.textMuted} style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dashboard.name}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        background: isActive ? 'rgba(99,102,241,0.25)' : theme.border,
                        color: isActive ? '#a78bfa' : theme.textMuted,
                        padding: '0 5px', borderRadius: 8
                      }}>
                        {dashboard.panels?.length || 0}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* New dashboard — pinned to right */}
          {canEdit && (
            <button
              onClick={() => setShowDashboardModal(true)}
              style={{
                padding: '3px 7px', background: 'transparent',
                border: `1px solid ${theme.border}`, borderRadius: 6,
                color: theme.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', outline: 'none',
                flexShrink: 0
              }}
              title="New dashboard"
            >
              <Plus size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Sync Error Banner */}
      {syncError && (
        <div style={{
          padding: '10px 16px',
          background: 'rgba(245, 158, 11, 0.1)',
          borderBottom: `1px solid ${theme.warning}`,
          color: theme.warning,
          fontSize: '13px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0
        }}>
          <Database size={16} color={theme.warning} />
          {syncError}
        </div>
      )}

      {/* Dashboard Filters Bar */}
      {currentDashboard && (
        <DashboardFiltersBar
          variables={dashboardVariables}
          variableValues={variableValues}
          variableOptions={variableOptions}
          variableOptionsLoading={variableOptionsLoading}
          onVariableChange={handleVariableChange}
          onRefreshVariables={() => fetchAllVariableOptions(dashboardVariables, currentDashboard)}
          adhocFilters={adhocFilters}
          adhocColumnOptions={adhocColumnOptions}
          onAddAdhocFilter={handleAddAdhocFilter}
          onUpdateAdhocFilter={handleUpdateAdhocFilter}
          onRemoveAdhocFilter={handleRemoveAdhocFilter}
          onOpenVariableManager={canEdit ? () => setShowVariableManager(true) : undefined}
          onShare={(canEdit || !authService.hasFeature('sharing')) && !currentDashboard?.sharedWith ? () => {
            if (authService.hasFeature('sharing')) setShowShareModal(true);
            else setShowShareLockedModal(true);
          } : undefined}
          shareLocked={!authService.hasFeature('sharing')}
          darkMode={darkMode}
          theme={theme}
        />
      )}

      {/* Drill-through back-navigation chip */}
      {currentDashboard && drillHistory.length > 0 && (
        <div style={{
          padding: `4px ${windowWidth < 640 ? 8 : windowWidth < 1024 ? 10 : 12}px 0`,
          background: theme.bg
        }}>
          <button
            onClick={drillBack}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 11px', borderRadius: 999,
              background: 'rgba(139,92,246,0.12)',
              border: '1px solid rgba(139,92,246,0.4)',
              color: '#a78bfa', fontSize: 11, fontWeight: 600, cursor: 'pointer'
            }}
            title={drillHistory[drillHistory.length - 1]?.fromPanelTitle
              ? `Return to "${drillHistory[drillHistory.length - 1].name}" (drill origin: ${drillHistory[drillHistory.length - 1].fromPanelTitle})`
              : `Return to "${drillHistory[drillHistory.length - 1]?.name}"`}
          >
            ← Back to {drillHistory[drillHistory.length - 1]?.name || 'previous'}
            {drillHistory.length > 1 && (
              <span style={{
                background: 'rgba(139,92,246,0.28)', padding: '1px 6px',
                borderRadius: 999, fontSize: 10
              }}>
                {drillHistory.length}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Bookmarks + Theme picker — named snapshots + palette swap */}
      {currentDashboard && (
        <div style={{
          padding: `4px ${windowWidth < 640 ? 8 : windowWidth < 1024 ? 10 : 12}px 0`,
          background: theme.bg,
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <BookmarksBar
              bookmarks={currentDashboard.bookmarks || []}
              variableValues={variableValues}
              hiddenPanelIds={hiddenPanelIds}
              onChange={handleUpdateBookmarks}
              onApplyVariables={handleApplyBookmarkVariables}
              onApplyHiddenPanels={setHiddenPanelIds}
              darkMode={darkMode}
              readOnly={!canEdit}
            />
          </div>
          {hiddenPanelIds.length > 0 && (
            <button
              onClick={() => setHiddenPanelIds([])}
              title="Show all hidden panels"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 999,
                background: 'rgba(139,92,246,0.14)',
                border: '1px solid rgba(139,92,246,0.4)',
                color: '#a78bfa', fontSize: 11, fontWeight: 600, cursor: 'pointer'
              }}
            >
              {hiddenPanelIds.length} hidden · show
            </button>
          )}
          <ThemePicker
            themePalette={currentDashboard.themePalette}
            themeName={currentDashboard.themeName}
            onChange={handleUpdateTheme}
            darkMode={darkMode}
            readOnly={!canEdit}
          />
        </div>
      )}

      {/* Pages / tabs — hidden when there's only the implicit single page */}
      {currentDashboard && (normalizePages(currentDashboard.pages || []).length > 1 || canEdit) && (
        <div style={{ background: theme.bg }}>
          <PagesTabBar
            pages={currentDashboard.pages || []}
            activePageId={activePageId}
            onChangeActive={setActivePageId}
            onAddPage={handleAddPage}
            onRenamePage={handleRenamePage}
            onDeletePage={handleDeletePage}
            darkMode={darkMode}
            readOnly={!canEdit}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: windowWidth < 640 ? '8px' : windowWidth < 1024 ? '10px' : '12px',
        background: theme.bg
      }}>
        {!currentDashboard || currentDashboard.panels.length === 0 ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            padding: windowWidth < 640 ? '16px' : '0'
          }}>
            <div style={{ maxWidth: windowWidth < 640 ? '320px' : '480px' }}>
              <div style={{
                width: windowWidth < 640 ? '60px' : '80px',
                height: windowWidth < 640 ? '60px' : '80px',
                background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.accentHover} 100%)`,
                borderRadius: windowWidth < 640 ? '16px' : '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                opacity: 0.9
              }}>
                <Layers size={windowWidth < 640 ? 28 : 36} color="white" />
              </div>
              <h2 style={{
                fontSize: windowWidth < 640 ? '20px' : '24px',
                fontWeight: '700',
                marginBottom: '12px',
                color: theme.text,
                letterSpacing: '-0.02em'
              }}>
                {currentDashboard ? 'Start Building Your Dashboard' : 'Welcome to Miralys'}
              </h2>
              <p style={{
                fontSize: windowWidth < 640 ? '13px' : '14px',
                color: theme.textMuted,
                marginBottom: '28px',
                lineHeight: '1.6'
              }}>
                {!canEdit
                  ? (currentDashboard ? 'This dashboard has no panels yet.' : 'No dashboards have been shared with you yet. Contact your administrator.')
                  : currentDashboard
                    ? 'Add data panels and SCADA diagrams to visualize your real-time data streams.'
                    : 'Create your first dashboard to organize visualizations and monitor your systems.'}
              </p>
              {canEdit && (
              <div style={{
                display: 'flex',
                gap: windowWidth < 640 ? '8px' : '10px',
                justifyContent: 'center',
                flexDirection: windowWidth < 640 ? 'column' : 'row'
              }}>
                <button
                  onClick={currentDashboard ? handleAddPanel : () => setShowDashboardModal(true)}
                  style={{
                    padding: windowWidth < 640 ? '10px 20px' : '12px 24px',
                    background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.accentHover} 100%)`,
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: windowWidth < 640 ? '13px' : '14px',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: darkMode
                      ? '0 4px 16px rgba(99, 102, 241, 0.4)'
                      : '0 2px 12px rgba(99, 102, 241, 0.3)',
                    width: windowWidth < 640 ? '100%' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = darkMode
                      ? '0 6px 20px rgba(99, 102, 241, 0.5)'
                      : '0 4px 16px rgba(99, 102, 241, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = darkMode
                      ? '0 4px 16px rgba(99, 102, 241, 0.4)'
                      : '0 2px 12px rgba(99, 102, 241, 0.3)';
                  }}
                >
                  <Plus size={windowWidth < 640 ? 16 : 18} color="white" />
                  {currentDashboard ? 'Add Data Panel' : 'Create Dashboard'}
                </button>
                {currentDashboard && (
                  <button
                    onClick={handleAddScada}
                    style={{
                      padding: windowWidth < 640 ? '10px 20px' : '12px 24px',
                      background: `linear-gradient(135deg, ${theme.success} 0%, #059669 100%)`,
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: windowWidth < 640 ? '13px' : '14px',
                      fontWeight: '600',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      boxShadow: darkMode
                        ? '0 4px 16px rgba(16, 185, 129, 0.4)'
                        : '0 2px 12px rgba(16, 185, 129, 0.3)',
                      width: windowWidth < 640 ? '100%' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = darkMode
                        ? '0 6px 20px rgba(16, 185, 129, 0.5)'
                        : '0 4px 16px rgba(16, 185, 129, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = darkMode
                        ? '0 4px 16px rgba(16, 185, 129, 0.4)'
                        : '0 2px 12px rgba(16, 185, 129, 0.3)';
                    }}
                  >
                    <Layers size={windowWidth < 640 ? 16 : 18} color="white" />
                    Add SCADA Diagram
                  </button>
                )}
              </div>
              )}
            </div>
          </div>
        ) : (
          <div ref={gridContainerRef}>
            <GridLayout
              layout={isMobile
                ? (() => {
                    // Mobile: stack every panel full-width in a single column
                    let cursorY = 0;
                    return visiblePanels.map(p => {
                      const h = p.height ?? 2;
                      const item = { i: p.id, x: 0, y: cursorY, w: GRID_COLS, h, minW: GRID_COLS, minH: 1, static: true };
                      cursorY += h;
                      return item;
                    });
                  })()
                : visiblePanels.map(p => ({
                    i: p.id,
                    x: p.x ?? 0,
                    y: p.y ?? 0,
                    w: Math.min(p.width ?? 4, GRID_COLS),
                    h: p.height ?? 2,
                    minW: 2,
                    minH: 1,
                  }))}
              cols={GRID_COLS}
              rowHeight={ROW_HEIGHT}
              width={gridWidth}
              margin={[windowWidth < 640 ? 8 : windowWidth < 1024 ? 10 : 12, windowWidth < 640 ? 8 : windowWidth < 1024 ? 10 : 12]}
              containerPadding={[0, 0]}
              onDragStop={handleLayoutChange}
              onResizeStop={handleLayoutChange}
              draggableHandle=".panel-drag-handle"
              resizeHandles={['se']}
              isDraggable={canEdit && windowWidth >= 640}
              isResizable={canEdit && windowWidth >= 640}
            >
            {visiblePanels.map((panel) => (
              <div key={panel.id} style={{ position: 'relative' }}>
                {panel.type === 'slicer' ? (
                  <SlicerPanel
                    config={panel}
                    plantId={currentDashboard?.plantId}
                    darkMode={darkMode}
                    readOnly={!canEdit}
                    onEdit={canEdit ? handleEdit : undefined}
                    onDelete={canEdit ? handleDelete : undefined}
                    onDuplicate={canEdit ? handleDuplicate : undefined}
                  />
                ) : panel.type === 'scada' ? (
                  <div style={{
                    height: '100%',
                    background: theme.card,
                    borderRadius: '10px',
                    border: `1px solid ${theme.border}`,
                    overflow: 'hidden',
                    boxShadow: darkMode 
                      ? '0 2px 8px rgba(0,0,0,0.3)' 
                      : '0 1px 4px rgba(0,0,0,0.06)',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{
                      padding: '10px 12px',
                      borderBottom: `1px solid ${theme.border}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: theme.cardHover
                    }}>
                      <h3 className="panel-drag-handle" style={{
                        margin: 0,
                        fontSize: '13px',
                        fontWeight: '600',
                        color: theme.text,
                        cursor: 'grab',
                        flex: 1
                      }}>
                        {panel.title}
                      </h3>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={() => enterFullscreen(panel)}
                          title="Fullscreen (F)"
                          style={{
                            padding: '4px 8px',
                            background: 'rgba(99,102,241,0.15)',
                            border: `1px solid rgba(99,102,241,0.4)`,
                            color: '#818cf8',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}
                        >
                          <Maximize2 size={13} />
                          Full
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => handleEdit(panel)}
                            style={{
                              padding: '5px',
                              background: 'transparent',
                              border: 'none',
                              color: theme.textSecondary,
                              cursor: 'pointer',
                              borderRadius: '4px'
                            }}
                          >
                            <Settings size={14} color={theme.textSecondary} />
                          </button>
                        )}
                        {canEdit && (
                          <button
                            onClick={() => handleDuplicate(panel)}
                            style={{
                              padding: '5px',
                              background: 'transparent',
                              border: 'none',
                              color: theme.textSecondary,
                              cursor: 'pointer',
                              borderRadius: '4px'
                            }}
                          >
                            <Plus size={14} color={theme.textSecondary} />
                          </button>
                        )}
                        {canEdit && (
                          <button
                            onClick={() => handleDelete(panel.id)}
                            style={{
                              padding: '5px',
                              background: 'transparent',
                              border: 'none',
                              color: theme.danger,
                              cursor: 'pointer',
                              borderRadius: '4px'
                            }}
                          >
                            <X size={14} color={theme.danger} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <ScadaPanel config={panel} darkMode={darkMode} plantId={currentDashboard?.plantId} onNavigate={handleNavigate} isActive={!showScadaModal && !isFullscreen} />
                    </div>
                  </div>
                ) : (
                  <div style={{ position: 'relative', height: '100%' }}>
                    <ConnectedQuestDBPanel
                      panelId={panel.id}
                      config={panel}
                      plantId={currentDashboard?.plantId}
                      onEdit={canEdit ? handleEdit : undefined}
                      onDelete={canEdit ? handleDelete : undefined}
                      onDuplicate={canEdit ? handleDuplicate : undefined}
                      readOnly={!canEdit}
                      darkMode={darkMode}
                      dashboardVariables={dashboardVariables}
                      variableValues={variableValues}
                      adhocFilters={adhocFilters}
                      themePalette={currentDashboard?.themePalette}
                      onHidePanel={(id) => setHiddenPanelIds(prev => [...new Set([...prev, id])])}
                    />
                  </div>
                )}
              </div>
            ))}
            </GridLayout>
          </div>
        )}
      </div>

      {/* Fullscreen overlay */}
      {isFullscreen && currentDashboard && (() => {
        // SCADA-only dashboard → show the single SCADA panel filling the whole screen (no header)
        // Dashboard with QuestDB panels → show the full panel grid
        const hasQuestDB = (currentDashboard.panels || []).some(p => p.type !== 'scada');
        const scadaPanelToShow = fullscreenPanel || (currentDashboard.panels || []).find(p => p.type === 'scada');

        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: theme.bg, display: 'flex', flexDirection: 'column' }}>
            {/* Top bar */}
            <div style={{
              background: theme.card, borderBottom: `1px solid ${theme.border}`,
              display: 'flex', alignItems: 'center', height: '48px', padding: '0 14px', gap: '10px', flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <div style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src="/vite.svg" alt="Miralys" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <span style={{ fontSize: '15px', fontWeight: '700', letterSpacing: '-0.02em', color: theme.text }}>Miralys</span>
              </div>

              <div style={{ width: '1px', height: '24px', background: theme.border, flexShrink: 0 }} />

              {/* Dashboard tabs */}
              <div style={{ display: 'flex', gap: '4px', flex: 1, overflow: 'hidden' }}>
                {dashboards.map(d => (
                  <button
                    key={d.id}
                    onClick={() => {
                      handleSelectDashboard(d);
                      // If switching to a SCADA-only dashboard, update the stored fullscreen panel
                      const dHasQuestDB = (d.panels || []).some(p => p.type !== 'scada');
                      if (!dHasQuestDB) {
                        const firstScada = (d.panels || []).find(p => p.type === 'scada');
                        if (firstScada) setFullscreenPanel(firstScada);
                      } else {
                        setFullscreenPanel(null);
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      background: currentDashboard?.id === d.id ? theme.cardHover : 'transparent',
                      color: currentDashboard?.id === d.id ? theme.text : theme.textMuted,
                      borderBottom: currentDashboard?.id === d.id ? `2px solid ${theme.accent}` : '2px solid transparent',
                      border: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                      fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: '6px',
                      borderRadius: '6px 6px 0 0', cursor: 'pointer'
                    }}
                  >
                    <Grid size={13} />
                    {d.name}
                    {d.sharedWith && (
                      <span style={{
                        fontSize: '9px', background: '#6366f122', color: '#6366f1',
                        padding: '1px 5px', borderRadius: '8px', fontWeight: '700'
                      }}>
                        {d.shareRole === 'editor' ? 'editor' : 'shared'}
                      </span>
                    )}
                    {d.panels?.length > 0 && (
                      <span style={{
                        fontSize: '10px', background: currentDashboard?.id === d.id ? theme.accent : theme.border,
                        color: currentDashboard?.id === d.id ? 'white' : theme.textMuted,
                        padding: '1px 5px', borderRadius: '10px', fontWeight: '700'
                      }}>
                        {d.panels.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <button onClick={exitFullscreen} title="Exit fullscreen (Esc or Enter)" style={{
                padding: '5px 12px', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.4)',
                color: '#818cf8', cursor: 'pointer', borderRadius: '6px',
                display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', flexShrink: 0
              }}>
                <Minimize2 size={14} />
                Exit
              </button>
            </div>

            {/* Content: single SCADA panel or full grid */}
            {!hasQuestDB && scadaPanelToShow ? (
              // SCADA-only dashboard — panel fills all remaining space, no header
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <ScadaPanel config={scadaPanelToShow} darkMode={darkMode} plantId={currentDashboard?.plantId} onNavigate={handleNavigate} isActive={!showScadaModal} />
              </div>
            ) : (
              // Mixed / QuestDB dashboard — show full panel grid
              <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
                {visiblePanels.length === 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.textMuted, fontSize: '14px' }}>
                    No panels on this page
                  </div>
                ) : (
                  <GridLayout
                    layout={visiblePanels.map(p => ({
                      i: p.id, x: p.x ?? 0, y: p.y ?? 0,
                      w: Math.min(p.width ?? 4, GRID_COLS), h: p.height ?? 2, minW: 2, minH: 1
                    }))}
                    cols={GRID_COLS}
                    rowHeight={ROW_HEIGHT}
                    width={window.innerWidth - 24}
                    margin={[12, 12]}
                    containerPadding={[0, 0]}
                    isDraggable={false}
                    isResizable={false}
                  >
                    {visiblePanels.map(panel => (
                      <div key={panel.id} style={{ position: 'relative' }}>
                        {panel.type === 'slicer' ? (
                          <SlicerPanel
                            config={panel}
                            plantId={currentDashboard?.plantId}
                            darkMode={darkMode}
                            readOnly={!canEdit}
                          />
                        ) : panel.type === 'scada' ? (
                          <div style={{
                            height: '100%', background: theme.card, borderRadius: '10px',
                            border: `1px solid ${theme.border}`, overflow: 'hidden',
                            boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 4px rgba(0,0,0,0.06)',
                            display: 'flex', flexDirection: 'column'
                          }}>
                            <div style={{ padding: '8px 12px', borderBottom: `1px solid ${theme.border}`, background: theme.cardHover, flexShrink: 0 }}>
                              <span style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>{panel.title}</span>
                            </div>
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <ScadaPanel config={panel} darkMode={darkMode} plantId={currentDashboard?.plantId} onNavigate={handleNavigate} isActive={!showScadaModal} />
                            </div>
                          </div>
                        ) : (
                          <div style={{ position: 'relative', height: '100%' }}>
                            <ConnectedQuestDBPanel
                              panelId={panel.id}
                              config={panel} plantId={currentDashboard?.plantId}
                              onEdit={canEdit ? handleEdit : undefined}
                              onDelete={canEdit ? handleDelete : undefined}
                              onDuplicate={canEdit ? handleDuplicate : undefined}
                              readOnly={!canEdit}
                              darkMode={darkMode} dashboardVariables={dashboardVariables}
                              variableValues={variableValues} adhocFilters={adhocFilters}
                              themePalette={currentDashboard?.themePalette}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </GridLayout>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Modals */}
      {showConfigModal && (
        <PanelConfigModal
          panel={editingPanel}
          onSave={handleSavePanel}
          onClose={() => {
            setShowConfigModal(false);
            setEditingPanel(null);
          }}
          allTables={availableTables}
          darkMode={darkMode}
          plantId={currentDashboard?.plantId}
          dashboards={dashboards}
          currentDashboardId={currentDashboard?._id || currentDashboard?.id}
          dashboardVariables={dashboardVariables}
          variableValues={variableValues}
        />
      )}

      {showDashboardModal && (
        <DashboardModal
          currentDashboard={currentDashboard}
          dashboards={dashboards}
          onSelect={handleSelectDashboard}
          onCreate={handleCreateDashboard}
          onRename={handleRenameDashboard}
          onDelete={handleDeleteDashboard}
          onClose={() => setShowDashboardModal(false)}
          darkMode={darkMode}
          userPlantAccess={
            (user?.plantAccess && user.plantAccess.length > 0)
              ? user.plantAccess
              : user?.plantId
                ? [{ plantId: user.plantId, role: 'operator' }]
                : []
          }
        />
      )}

      {showScadaModal && (
        <ScadaDesigner
          config={editingScadaDiagram}
          onSave={handleSavePanel}
          onAutoSave={handleAutoSavePanel}
          onClose={() => {
            setShowScadaModal(false);
            setEditingScadaDiagram(null);
          }}
          darkMode={darkMode}
          plantId={currentDashboard?.plantId}
          availablePanels={allScadaPanels}
        />
      )}

      <SlicerConfigModal
        isOpen={showSlicerModal}
        editingSlicer={editingSlicer}
        plantId={currentDashboard?.plantId}
        darkMode={darkMode}
        onSave={handleSaveSlicer}
        onClose={() => { setShowSlicerModal(false); setEditingSlicer(null); }}
      />

      {showVariableManager && (
        <VariableManager
          variables={dashboardVariables}
          onSave={handleSaveVariables}
          onClose={() => setShowVariableManager(false)}
          darkMode={darkMode}
          plantId={currentDashboard?.plantId}
          availableTables={availableTables}
        />
      )}

      {showShareModal && currentDashboard && (
        <ShareDashboardModal
          dashboard={currentDashboard}
          darkMode={darkMode}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showShareLockedModal && (
        <RequestAccessModal
          feature="sharing"
          featureLabel="Dashboard Sharing"
          onClose={() => setShowShareLockedModal(false)}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .hdr-btn {
          display: inline-flex;
          align-items: center;
          gap: 0px;
          overflow: hidden;
          white-space: nowrap;
          outline: none;
          cursor: pointer;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          transition: gap 0.18s ease, padding 0.18s ease, max-width 0.22s ease;
          max-width: 32px;
          padding: 5px 8px;
          flex-shrink: 0;
        }
        .hdr-btn:hover {
          gap: 5px;
          max-width: 120px;
          padding: 5px 11px;
        }
        .hdr-btn .btn-label {
          max-width: 0;
          overflow: hidden;
          opacity: 0;
          transition: max-width 0.2s ease, opacity 0.15s ease;
          white-space: nowrap;
        }
        .hdr-btn:hover .btn-label {
          max-width: 80px;
          opacity: 1;
        }
        .hdr-btn-ghost {
          background: transparent;
          border: 1px solid ${theme.border};
          color: ${theme.textSecondary};
        }
        .hdr-btn-ghost:hover {
          background: ${theme.hover};
          color: ${theme.text};
        }
        
        * {
          scrollbar-width: thin;
          scrollbar-color: ${theme.border} transparent;
        }
        
        *::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        
        *::-webkit-scrollbar-track {
          background: transparent;
        }
        
        *::-webkit-scrollbar-thumb {
          background: ${theme.border};
          border-radius: 3px;
        }
        
        *::-webkit-scrollbar-thumb:hover {
          background: ${theme.borderLight};
        }
      `}</style>
    </div>
    </DashboardFilterProvider>
  );
}